import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { getXProductHeader } from '@atlassian/editor-ai-common/api/request';

import type { AIPlugin } from '../../aiPluginType';
import type { EditorPluginAIProvider } from '../../types/types';
import {
	shouldImproveWritingApiUrl,
	shouldImproveWritingCcSmartsApiUrl,
} from '../../ui/prebuilt/utils';

import { fireAiQuickPromptApiErrorEvent } from './analytics';
import type { EligibleRange } from './types';

/**
 * Call the should-improve-writing API to check whether the content in the
 * given range should be improved before showing the AI quick prompt.
 *
 * Fires an `apiError` analytics event when a network or HTTP error occurs.
 *
 * @returns `true` when the API confirms the content should be improved,
 *          `false` otherwise (including network / parse errors).
 */
export async function checkShouldImproveWriting({
	api,
	experienceName,
	locale,
	product,
	range,
	signal,
	view,
}: {
	api?: ExtractInjectionAPI<AIPlugin>;
	experienceName: string;
	locale: string;
	product: EditorPluginAIProvider['product'];
	range: EligibleRange;
	/** Optional abort signal to cancel the network request early. */
	signal?: AbortSignal;
	view: EditorView;
}): Promise<boolean> {
	try {
		const { doc, schema } = view.state;
		const $from = doc.resolve(range.from);
		const parentNode = $from.parent;
		const docNode = schema.nodes.doc.createAndFill(null, parentNode);
		if (!docNode) {
			return false;
		}

		const transformer = new JSONTransformer();
		const adfContent = JSON.stringify(transformer.encode(docNode));

		const experienceId = 'editor';

		// Routing: the SPRT experiment cc_smarts_should_improve_writing_migration
		// (param useCcSmarts) decides whether this request goes to cc-smarts (new home)
		// or to conversational-ai-platform (current source of truth). Wire shape is
		// byte-for-byte identical between the two backends; only the URL changes.
		// Default false means: any SPRT read error keeps traffic on convo-ai (fail-closed).
		// See CCI-16830.
		// NOTE: narrow try/catch is intentional — a Statsig client error here is NOT an
		// API call failure and must not fire fireAiQuickPromptApiErrorEvent (would pollute
		// analytics and could trigger false alerts on the editor-ai dashboard).
		let useCcSmarts = false;
		try {
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			useCcSmarts = FeatureGates.getExperimentValue(
				'cc_smarts_should_improve_writing_migration',
				'useCcSmarts',
				false,
				{ typeGuard: (v: unknown): v is boolean => typeof v === 'boolean' },
			);
		} catch {
			// Fall back to convo-ai (default URL) without reporting an API error.
			useCcSmarts = false;
		}
		const url = useCcSmarts ? shouldImproveWritingCcSmartsApiUrl : shouldImproveWritingApiUrl;

		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json;charset=UTF-8',
				'X-Experience-Id': experienceId,
				...getXProductHeader(product),
			},
			mode: 'cors',
			credentials: 'include',
			signal,
			body: JSON.stringify({
				ai_feature_input: {
					locale,
					adf_content: adfContent,
				},
			}),
		});

		if (!response.ok) {
			fireAiQuickPromptApiErrorEvent({ api, experienceName, statusCode: response.status });
			return false;
		}

		const data = await response.json();
		return data?.ai_feature_output?.should_improve === true;
	} catch (error) {
		if (error instanceof Error && error.name === 'AbortError') {
			return false;
		}
		fireAiQuickPromptApiErrorEvent({
			api,
			experienceName,
			errorMessage: error instanceof Error ? error.message : undefined,
		});
		return false;
	}
}
